import {BasicFunnelStep, FunnelStep, IncompleteBuckets, TrendUnit} from 'common/report/funnels/models/types';
import {FunnelBookmark} from 'common/report/funnels/models/types';
import {QueryTrackingProps} from 'common/report/util/query-tracking/types';
import {SankeyAnchorType, SankeyNodeType} from 'common/widgets/sankey-chart/models/types';

export interface BasicQueryApiParams {
  bookmark: FunnelBookmark;
  funnel_id: number;
  /** format should be CSV or undefined */
  format?: string;
  query_type: string;
  /** title is available when downloading PDF/CSV, else undefined */
  title?: string;
  use_query_cache: boolean;
  tracking_props: QueryTrackingProps;
}

export interface ApiParams extends BasicQueryApiParams {
  projectId: string;
  workspaceId?: string;
}

interface BasicQueryResponse {
  computed_at: string;
  min_sampling_factor?: number;
  meta?: any;
  metadata?: any;
  data?: any;
  error?: string;
  message?: string;
}

export interface ErrorResponse extends BasicQueryResponse {
  error: string;
  message?: string;
  data?: any;
  statusCode?: number;
}

interface Analysis {
  completion: number;
  starting_amount: number;
  steps: number;
  worst: number;
}

/** A nested map of segments, with Array<FunnelStep> leaf nodes. */
export interface SegmentedFunnelResults {
  $overall?: Array<BasicFunnelStep | FunnelStep>;
  [segment: string]: SegmentedFunnelResults | Array<BasicFunnelStep | FunnelStep>;
}

export interface UnsegmentedFunnelResult {
  analysis: Analysis;
  steps: BasicFunnelStep[];
}

export interface UnsegmentedDetailedFunnelResult {
  analysis: Analysis;
  steps: Array<BasicFunnelStep | FunnelStep>;
}

export type KeyedByDate<T> = {
  [date: string]: T;
};

export interface RegularQueryResponse extends BasicQueryResponse {
  data: {
    [date: string]: UnsegmentedFunnelResult;
  };
  compare_data?: {
    [date: string]: UnsegmentedFunnelResult;
  };
  lift_data?: {
    [date: string]: UnsegmentedFunnelResult;
  };
}

export interface DetailedQueryResponse extends BasicQueryResponse {
  data: {
    [date: string]: UnsegmentedDetailedFunnelResult;
  };
  compare_data?: {
    [date: string]: UnsegmentedDetailedFunnelResult;
  };
  lift_data?: {
    [date: string]: UnsegmentedFunnelResult;
  };
}

export interface SegmentedQueryResponse extends BasicQueryResponse {
  data: {
    [date: string]: SegmentedFunnelResults;
  };

  compare_data?: {
    [date: string]: SegmentedFunnelResults;
  };

  lift_data?: {
    [date: string]: SegmentedFunnelResults;
  };

  meta: {
    property_values: string[];
    dates: string[];
  };
}

export interface TrendsQueryResponse extends BasicQueryResponse {
  meta: {
    dates: string[];
    // TODO (lkloh): Do not use, we will remove this once the trend_unit is deleted from the backend response.
    trend_unit?: TrendUnit;
    incomplete_buckets: IncompleteBuckets;
  };
  data: {
    [date: string]: UnsegmentedFunnelResult;
  };

  compare_data?: {
    [date: string]: UnsegmentedFunnelResult;
  };

  lift_data?: {
    [date: string]: UnsegmentedFunnelResult;
  };
}

export interface SegmentedTrendsQueryResponse extends BasicQueryResponse {
  meta: {
    property_values: string[];
    dates: string[];
    incomplete_buckets: IncompleteBuckets;
  };
  data: {
    [date: string]: {
      [segment: string]: Array<BasicFunnelStep | FunnelStep>;
    };
  };

  compare_data?: {
    [date: string]: SegmentedFunnelResults;
  };
}

export interface SankeyEdge {
  breakdowns: any;
  event: string;
  isComputed: boolean;
  segments: string[];
  totalCount: number;
  type: SankeyNodeType;
  step: number;
  anchorType: SankeyAnchorType;
}

export interface SankeyNode {
  breakdowns: any;
  edges: SankeyEdge[];
  event: string;
  isComputed: boolean;
  segments: string[];
  totalCount: number;
  type: SankeyNodeType;
  isCustomEvent: boolean;
  anchorType: SankeyAnchorType;
  conversionRateChange?: number;
}

export interface SankeyStep {
  nodes: SankeyNode[];
}

export interface SankeyData {
  breakdowns: any;
  steps: SankeyStep[];
}

export interface SankeyResponse extends BasicQueryResponse {
  meta: {
    min_sampling_factor: number;
  };
  data: {
    [date: string]: SankeyData;
  };
}

export interface FlowsSankeyResponse extends BasicQueryResponse {
  metadata: {
    min_sampling_factor: number;
  };
  breakdowns: any;
  steps: SankeyStep[];
}

export interface TopPathsFlowStep {
  event: string;
  type: SankeyNodeType;
  isComputed: boolean;
  segments?: TopPathsFlowSegment[];
  totalCount: string;
}

export interface TopPathsFlowSegment {
  label: string;
  value: string;
  values: string[];
}

export interface TopPathsFlow {
  flowSteps: TopPathsFlowStep[];
  segments: TopPathsFlowSegment[];
}

export interface FlowsTopPathsResponse extends BasicQueryResponse {
  metadata: {
    min_sampling_factor: number;
  };
  flows: TopPathsFlow[];
}

export type FlowsResponse = FlowsSankeyResponse | FlowsTopPathsResponse;

export type FunnelQueryResponse =
  | RegularQueryResponse
  | DetailedQueryResponse
  | SegmentedQueryResponse
  | TrendsQueryResponse;

export type QueryResponse = FunnelQueryResponse | SankeyResponse | ErrorResponse;

export enum RequestStatus {
  Main = `request_status_data`,
  Trends = `request_status_trend`,
  Sankey = `request_status_sankey`,
}

export enum RequestState {
  Loading = `loading`,
  Complete = `complete`,
  Error = `error`,
}

export enum QueryType {
  Main = `funnels_report`,
  Trends = `funnels_report_segmented_trends`,
  TimeToConvert = `funnels_report_time_to_convert`,
  Sankey = `funnels_any_order_sankey`,
  DropOff = `flows`,
  Frequency = `funnels_report_frequency`,
  ShortLink = `short_link`,
}

export function isErrorResponse(r: QueryResponse): r is ErrorResponse {
  return `error` in r;
}

export function isSegmented(r: QueryResponse): r is SegmentedQueryResponse | SegmentedTrendsQueryResponse {
  const firstDataKey = Object.keys(r.data)[0];
  return !(`analysis` in r.data[firstDataKey]);
}

/**
 * Determines if a given funnel segment is segmented or not based on the following response structure:
 *
 * Segmented data:
 *  {
 *    "YYYY-MM-DD": {
 *      "$overall": [...]
 *      "<PROPERTY>": [...]
 *    }
 *  }
 * Unsegmented data:
 *  {
 *    "YYYY-MM-DD": {
 *      "steps": [...],
 *      "analysis": {...}
 *    }
 *  }
 *
 * Note: This also covers comparison steps
 */
export function isSegmentedData(
  segment: UnsegmentedFunnelResult | SegmentedFunnelResults,
): segment is SegmentedFunnelResults {
  return !(Object.keys(segment).length === 2 && `steps` in segment && `analysis` in segment);
}

export function isSankeyResponse(q: QueryType, _r: QueryResponse): _r is SankeyResponse {
  return q === QueryType.Sankey;
}

export function isTrendsResponse(q: QueryType, _r: QueryResponse): _r is TrendsQueryResponse {
  return q === QueryType.Trends;
}

export function isRegularResponse(q: QueryType, _r: QueryResponse): _r is RegularQueryResponse {
  return q === QueryType.Main;
}

export function isTimeToConvertResponse(q: QueryType, _r: QueryResponse): _r is RegularQueryResponse {
  return q === QueryType.TimeToConvert;
}

export function isFrequencyResponse(q: QueryType, _r: QueryResponse): _r is RegularQueryResponse {
  return q === QueryType.Frequency;
}

export function isFunnelStepArray(d: SegmentedFunnelResults['segment']): d is Array<BasicFunnelStep | FunnelStep> {
  return Array.isArray(d);
}
